import { DateTime } from "luxon"
import { ICON } from "../../components/Icon/data"
import { FORMAT_TYPE } from "../../data/variables"
import i18n from "../../i18n"
import { isValue } from "../../services/utils/misc"

type Field = {
  label?: string
  type: 'text' | 'map' | 'datetime'
  key: string
}

type Info = {
  key: string
  fields: Field[]
}

enum Column {
  VEHICLE = 'VEHICLE',
  EMISSIONS = 'EMISSIONS',
  DRIVER = 'DRIVER',
  LOCATION = 'LOCATION',
  TIME = 'TIME',
  TIMESTAMP = 'TIMESTAMP',
  SERVICE = 'SERVICE'
}

type TableColumn = {
  id: Column,
  key: string,
  label: string,
  formatFunction?: (data: string | number) => string | number
}

type DetailData = {
  hasGraph?: boolean
  hasTable?: boolean
  graphDays?: number
  key: string
  path?: string
  info?: Info
  columns?: TableColumn[]
  showRow?: (data: any) => boolean
}

type CardMap = {
  [key: string]: {
    icon: string
    text: string
    unit?: string
    path?: string
    format?: string
    showArrow?: boolean
  }
}

export enum CARD_KEYS {
  MAX_SPEED_LIMIT = 'max_speed_over_limit',
  AVG_SPEED_LIMIT = 'avg_max_speed_over_limit',
  SPEED_VIOLATIONS = 'speed_violations',
  ACTIVE_VEHICLES = 'active',
  HOURS_IDLE = 'idle',
  DISTANCE_DRIVEN = 'distance',
  AVG_MAX_SPEED = 'avg_max_speed',
  HOURS_STOP = 'stop_duration',
  MAX_SPEED = 'max_speed',
  FLEET_DRIVING_QUALITY = 'avg_speeding_grade',
  DUE_FOR_MAINTENANCE = 'maintenance',
  CO2_EMISSIONS = 'emissions'
}

export const CARD_MAP: CardMap = {
  [CARD_KEYS.MAX_SPEED_LIMIT]: {
    icon: ICON.ROUND_METER,
    text: i18n.t('max speed over limit'),
    unit: i18n.t('km/h'),
    format: FORMAT_TYPE.NUMBER,
    showArrow: false,
  },
  [CARD_KEYS.AVG_SPEED_LIMIT]: {
    icon: ICON.ROUND_METER,
    text: i18n.t('average max speed over limit'),
    unit: i18n.t('km/h'),
    format: FORMAT_TYPE.NUMBER,
    showArrow: false,
  },
  [CARD_KEYS.SPEED_VIOLATIONS]: {
    icon: ICON.ROUND_METER,
    text: i18n.t('speed violations'),
    format: FORMAT_TYPE.NUMBER,
    showArrow: true,
  },
  [CARD_KEYS.ACTIVE_VEHICLES]: {
    icon: ICON.TRAFFIC_EVENT,
    text: i18n.t('active vehicles'),
    format: FORMAT_TYPE.NUMBER,
    showArrow: true,
    path: 'avg_active_mobiles_per_day'
  },
  [CARD_KEYS.HOURS_IDLE]: {
    icon: ICON.PAUSE,
    text: i18n.t('hours idle'),
    format: FORMAT_TYPE.DURATION,
    showArrow: true,
    path: 'idle_seconds'
  },
  [CARD_KEYS.DISTANCE_DRIVEN]: {
    icon: ICON.ROAD,
    text: i18n.t('distance driven'),
    unit: 'km',
    format: FORMAT_TYPE.NUMBER,
    showArrow: true,
    path: 'distance_total_km'
  },
  [CARD_KEYS.AVG_MAX_SPEED]: {
    icon: ICON.ROUND_METER,
    text: i18n.t('average max speed'),
    unit: i18n.t('km/h'),
    format: FORMAT_TYPE.NUMBER,
    showArrow: false,
  },
  [CARD_KEYS.HOURS_STOP]: {
    icon: ICON.PAUSE,
    text: i18n.t('hours stop'),
    format: FORMAT_TYPE.DURATION,
    showArrow: true,
    path: 'stop_duration_sec'
  },
  [CARD_KEYS.MAX_SPEED]: {
    icon: ICON.ROUND_METER,
    text: i18n.t('max speed'),
    unit: i18n.t('km/h'),
    format: FORMAT_TYPE.ONE_DECIMAL,
    showArrow: false,
  },
  [CARD_KEYS.FLEET_DRIVING_QUALITY]: {
    icon: ICON.CARS,
    text: i18n.t('Fleet driving quality'),
    format: FORMAT_TYPE.LETTER_GRADE,
    showArrow: true,
  },
  [CARD_KEYS.DUE_FOR_MAINTENANCE]: {
    icon: ICON.TOOL_KIT,
    text: i18n.t('Vehicles due for maintenance'),
    showArrow: false,
    path: 'total_vehicles_due_for_maintenance'
  },
  [CARD_KEYS.CO2_EMISSIONS]: {
    icon: ICON.CO2,
    text: i18n.t('CO2 emissions'),
    unit: 'kg',
    format: FORMAT_TYPE.NUMBER,
    showArrow: true,
    path: 'emissions_total_kg'
  }
}

const TABLE_COLUMNS = {
  [Column.VEHICLE]: {
    id: Column.VEHICLE,
    key: 'globalid',
    label: i18n.t('Vehicle')
  },
  [Column.EMISSIONS]: {
    id: Column.EMISSIONS,
    key: 'idle_seconds_interval',
    label: i18n.t('Emissions'),
    formatFunction: (seconds: number) => (seconds / 60 / 60).toFixed(1)
  },
  [Column.DRIVER]: {
    id: Column.DRIVER,
    key: 'driver',
    label: i18n.t('Driver')
  },
  [Column.LOCATION]: {
    id: Column.LOCATION,
    key: 'location',
    label: i18n.t('Location')
  },
  [Column.TIME]: {
    id: Column.TIME,
    key: 'time',
    label: i18n.t('Time'),
    formatFunction: (time: string) => DateTime.fromISO(time).toFormat('LLL d, yyyy HH:mm')
  },
  [Column.TIMESTAMP]: {
    id: Column.TIMESTAMP,
    key: 'timestamp',
    label: i18n.t('Time')
  },
  [Column.SERVICE]: {
    id: Column.SERVICE,
    key: 'impending_service',
    label: i18n.t('Service')
  }
}
  
export const DETAIL_DATA: Record <CARD_KEYS, DetailData> = {
  [CARD_KEYS.MAX_SPEED_LIMIT]: {
    key: 'max_speed_over_limit',
    hasGraph: true,
    graphDays: 14,
    info: {
      key: 'max_speed_over_limit_detail',
      fields: [{
        label: i18n.t('Vehicle'),
        key: 'globalid',
        type: 'text'
      }, {
        label: i18n.t('Driver'),
        key: 'driver',
        type: 'text'
      }, {
        label: i18n.t('Location'),
        key: 'location',
        type: 'text',
      }, {
        label: i18n.t('Timestamp'),
        key: 'posdate',
        type: 'text',
      }, {
        key: 'poslat',
        type: 'map'
      }]
    }
  },
  [CARD_KEYS.AVG_SPEED_LIMIT]: {
    key: 'avg_max_speed_over_limit',
    hasGraph: true,
    graphDays: 14,
  },
  [CARD_KEYS.SPEED_VIOLATIONS]: {
    key: 'speed_violations',
    path: 'speed_violations',
    hasGraph: false,
    hasTable: true,
    columns: [
      TABLE_COLUMNS[Column.VEHICLE], TABLE_COLUMNS[Column.DRIVER], TABLE_COLUMNS[Column.LOCATION],
      TABLE_COLUMNS[Column.TIME],
    ]
  },
  [CARD_KEYS.ACTIVE_VEHICLES]: {
    key: 'active_vehicles',
    hasGraph: true,
    graphDays: 30
  },
  [CARD_KEYS.HOURS_IDLE]: {
    key: 'idle_seconds',
    path: 'mobiles',
    hasGraph: true,
    hasTable: true,
    graphDays: 14,
    columns: [
      TABLE_COLUMNS[Column.VEHICLE], TABLE_COLUMNS[Column.DRIVER], TABLE_COLUMNS[Column.TIMESTAMP],
    ]
  },
  [CARD_KEYS.DISTANCE_DRIVEN]: {
    key: 'distance_km',
    hasGraph: true,
    graphDays: 30
  },
  [CARD_KEYS.AVG_MAX_SPEED]: {
    key: 'avg_max_speed',
    hasGraph: true,
    graphDays: 14,
  },
  [CARD_KEYS.HOURS_STOP]: {
    key: 'stop_duration_sec',
    hasGraph: true,
    graphDays: 14,
  },
  [CARD_KEYS.MAX_SPEED]: {
    key: 'max_speed',
    hasGraph: true,
    graphDays: 14,
    info: {
      key: 'max_speed_detail',
      fields: [{
        label: i18n.t('Vehicle'),
        key: 'globalid',
        type: 'text'
      }, {
        label: i18n.t('Driver'),
        key: 'driver',
        type: 'text'
      }, {
        label: i18n.t('Location'),
        key: 'location',
        type: 'text',
      }, {
        label: i18n.t('Timestamp'),
        key: 'posdate',
        type: 'datetime',
      }, {
        key: 'poslat',
        type: 'map'
      }]
    }
  },
  [CARD_KEYS.FLEET_DRIVING_QUALITY]: {
    key: 'avg_speeding_grade',
    hasGraph: true,
    graphDays: 14,
  },
  [CARD_KEYS.DUE_FOR_MAINTENANCE]: {
    key: 'impending_service',
    path: 'mobiles',
    hasTable: true,
    columns: [
      TABLE_COLUMNS[Column.VEHICLE], TABLE_COLUMNS[Column.SERVICE]
    ],
    showRow: (data) => isValue(data.impending_service)
  },
  [CARD_KEYS.CO2_EMISSIONS]: {
    key: 'emissions_kg',
    path: 'mobiles',
    hasGraph: true,
    graphDays: 30,
    hasTable: true,
    columns: [
      TABLE_COLUMNS[Column.VEHICLE], TABLE_COLUMNS[Column.EMISSIONS]
    ],
    showRow: (data) => !!data?.idle_seconds_interval && data?.idle_seconds_interval >= 360
  }
}